import urlcat from 'urlcat';

import { type UUID } from '~/types/common';

/**
 * Paths for all endpoints of the VESTIGAS API used in the admin app.
 */
export const ENDPOINT = {
  ACCESS_CONTROL: {
    GRANT_ROLE: ({
      role,
      subjectId,
      subjectType,
      targetId,
      targetType,
    }: {
      role: string;
      subjectId: UUID;
      subjectType: string;
      targetId: UUID;
      targetType: string;
    }): string =>
      urlcat(
        'access_control/grant/role/:subjectType/:subjectId/:targetType/:targetId/:role',
        {
          role,
          subjectId,
          subjectType,
          targetId,
          targetType,
        },
      ),
  },
  ADMIN_MCP: {
    WRITE_TOOLS: () => 'vadmin/mcp/write_tools',
  },
  AI: {
    DOWNLOAD_FILE: (): string => 'download_file_direct',
    EVALUATE_MODEL_PERFORMANCE: () => 'evaluate',
    GET_DOCUMENTS_PER_PROJECT: () => 'get-documents-per-project',
    GET_FILTERED_MODELS: () => 'get-models',
    GET_FILTERED_PROJECTS: () => 'get-projects',
    GET_MAPPINGS_FIELDS: () => 'get-mappings-fields',
    GET_PROJECT_BY_MODEL: () => 'get-project-by-model',
    LABELING_POSTPROCESSING_REPORT: () => 'labeling_postprocessing_report',
    UPLOAD_PDF_TO_BLOB: () => 'upload_pdf_to_blob',
    VALIDATE_LABELING: () => 'labeling_validation',
    VALIDATE_LABELING_POSITION_CONSISTENCY: () =>
      'labeling_position_consistency',
  },
  ALEMBIC_VERSION: {
    GET: () => 'vadmin/alembic_version',
  },
  API_POLLER_SECRET: {
    CREATE: () => 'vadmin/api_poller/secret',
    GET: (secretId: UUID): string =>
      urlcat('vadmin/api_poller/secret/:secretId', { secretId }),
    GET_ALL: () => 'vadmin/api_poller/secret',
    GET_POLLER_NAMES: () => 'vadmin/api_poller/secret/poller_names',
    UPDATE: (secretId: UUID): string =>
      urlcat('vadmin/api_poller/secret/:secretId', { secretId }),
    UPDATE_ACTIVE_STATUS: (secretId: UUID): string =>
      urlcat('vadmin/api_poller/secret/:secretId/active_status', { secretId }),
  },
  ASSET_REPARSING: {
    DELETE_REPARSING_ASSET: (reparsingId: UUID): string =>
      urlcat('vadmin/asset_reparsing/:reparsingId', { reparsingId }),
    GET_ALL: () => 'vadmin/asset_reparsing/all',
    GET_ORIGINAL_DLN_FILE: (filename: string): string =>
      urlcat('vadmin/asset_reparsing/original_dln_file', { filename }),
    REPARSE_ASSET: (reparsingId: UUID): string =>
      urlcat('vadmin/asset_reparsing/:reparsingId/parse', { reparsingId }),
    UPDATE_ORIGINAL_DLN_DATA: (reparsingId: UUID): string =>
      urlcat('vadmin/asset_reparsing/:reparsingId/update_original_dln_data', {
        reparsingId,
      }),
  },
  AUTH: {
    TOKENS: {
      GET: () =>
        'https://login.vestigas.com/auth/realms/vestigas/protocol/openid-connect/token',
    },
  },
  COMPANY: {
    ACCOUNT: {
      CREATE: () => 'company_account',
      DELETE: (companyAccountId: UUID): string =>
        urlcat('vadmin/company_accounts/:companyAccountId', {
          companyAccountId,
        }),
      DELETION_CHECK: (companyAccountId: UUID): string =>
        urlcat('vadmin/company_accounts/:companyAccountId/deletion_check', {
          companyAccountId,
        }),
      GET: (companyAccountId: UUID): string =>
        urlcat('company_account/:companyAccountId', { companyAccountId }),
      GET_ALL_ADMIN: () => 'vadmin/company_account',
      RESET_SIGNATURE_ROLES: (companyAccountId: UUID): string =>
        urlcat(
          'vadmin/company_account/:companyAccountId/reset_signature_roles',
          { companyAccountId },
        ),
      SUPPLIER_LETTER: {
        DELETE: (companyAccountId: UUID): string =>
          urlcat('vadmin/company_accounts/:companyAccountId/supplier_letter', {
            companyAccountId,
          }),
        GET: (companyAccountId: UUID): string =>
          urlcat('vadmin/company_accounts/:companyAccountId/supplier_letter', {
            companyAccountId,
          }),
        OVERRIDES: (companyAccountId: UUID): string =>
          urlcat(
            'vadmin/company_accounts/:companyAccountId/supplier_letter/overrides',
            { companyAccountId },
          ),
        PUT: (companyAccountId: UUID): string =>
          urlcat('vadmin/company_accounts/:companyAccountId/supplier_letter', {
            companyAccountId,
          }),
      },
      TRIGGER_SYNC: () => 'sync/trigger',
      UPDATE: (companyAccountId: UUID): string =>
        urlcat('company_account/:companyAccountId', { companyAccountId }),
    },
    ACTIVATION_ROUTING_DIAGNOSIS: {
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/activation_routing_diagnosis', {
          companyId,
        }),
    },
    ADDRESS: { CREATE: () => 'company_address' },
    ADDRESS_MAPPING: { CREATE: () => 'company_address' },
    AI_SETTINGS: {
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/default_ai_model_settings', {
          companyId,
        }),
      UPDATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/default_ai_model_settings', {
          companyId,
        }),
    },
    ALIAS_ADDRESS: {
      CREATE: (): string => 'vadmin/company/alias_address',
      DELETE: (aliasAddressId: UUID): string =>
        urlcat('vadmin/company/alias_address/:aliasAddressId', {
          aliasAddressId,
        }),
      GET: (aliasAddressId: UUID): string =>
        urlcat('vadmin/company/alias_address/:aliasAddressId', {
          aliasAddressId,
        }),
      GET_ALL: (): string => 'vadmin/company/alias_address',
      GET_COMPANY_FROM_ADDRESS: (): string =>
        'vadmin/company/alias_address/query',
      UPDATE: (aliasAddressId: UUID): string =>
        urlcat('vadmin/company/alias_address/:aliasAddressId', {
          aliasAddressId,
        }),
    },
    API_KEY: {
      CREATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/api_key', { companyId }),
      GET_ALL: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/api_key', { companyId }),
    },
    CHECK_DUPLICATE: () => 'vadmin/company/check_duplicate',
    CREATE: () => 'vadmin/company',
    DELETE: (companyId: UUID): string =>
      urlcat('vadmin/companies/:companyId', { companyId }),
    DELETION_CHECK: (companyId: UUID): string =>
      urlcat('vadmin/companies/:companyId/deletion_check', { companyId }),
    DIRECTLY_SEND_INVOICES_TO: {
      GET_ALL: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/directly_send_invoices_to', {
          companyId,
        }),
      UPDATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/directly_send_invoices_to', {
          companyId,
        }),
    },
    GET_ADMIN: (companyId: UUID): string =>
      urlcat('vadmin/company/:companyId', { companyId }),
    GET_ALL_ADMIN: () => 'vadmin/company',
    GET_SUPPLIER_ALERT_CONFIGS: () => 'company/sending_companies',
    HUBSPOT_IMPORT: {
      APPLY: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/hubspot_import_apply', { companyId }),
      MATCH: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/hubspot_import', { companyId }),
    },
    IDENTIFIER: {
      CREATE: () => 'company/identifier',
      DELETE: (companyIdentifierId: UUID): string =>
        urlcat('company/identifier/:companyIdentifierId', {
          companyIdentifierId,
        }),
      GET_ALL: () => 'company/identifier',
    },
    INVOICE_ALLOWED_SENDER_ADDRESS: {
      CREATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/invoice_allowed_sender_address', {
          companyId,
        }),
      DELETE: (companyId: UUID, email: string): string =>
        urlcat(
          'vadmin/company/:companyId/invoice_allowed_sender_address/:email',
          {
            companyId,
            email,
          },
        ),
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/invoice_allowed_sender_address', {
          companyId,
        }),
    },
    INVOICE_CHECK_DELAY: {
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/invoice_check_delay', { companyId }),
      UPDATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/invoice_check_delay', { companyId }),
    },
    INVOICE_MAILBOX: {
      CREATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/invoice_mailbox', { companyId }),
      DELETE: (companyId: UUID, email: string): string =>
        urlcat('vadmin/company/:companyId/invoice_mailbox/:email', {
          companyId,
          email,
        }),
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/invoice_mailbox', { companyId }),
    },
    INVOICE_RECEIVERS: {
      CREATE: (companyId: UUID): string =>
        urlcat('company/:companyId/invoice_receivers', { companyId }),
      DELETE: (companyId: UUID, email: string): string =>
        urlcat('company/:companyId/invoice_receivers/:email', {
          companyId,
          email,
        }),
      GET_ALL: (companyId: UUID): string =>
        urlcat('company/:companyId/invoice_receivers', { companyId }),
      UPDATE: (companyId: UUID): string =>
        urlcat('company/:companyId/invoice_receivers', { companyId }),
    },
    INVOICE_SETTINGS: {
      GET: (companyId: UUID): string =>
        urlcat('company/:companyId/invoice_sending_settings', { companyId }),
      UPDATE: (companyId: UUID): string =>
        urlcat('company/:companyId/invoice_sending_settings', { companyId }),
    },
    LOGO: {
      DELETE: (companyId: UUID): string =>
        urlcat('company/:companyId/logo', { companyId }),
      GET: (companyId: UUID): string =>
        urlcat('company/:companyId/logo', { companyId }),
      UPDATE: (companyId: UUID): string =>
        urlcat('company/:companyId/logo', { companyId }),
    },
    MATCH: () => 'vadmin/company/match',
    MOVE: (companyId: UUID): string =>
      urlcat('vadmin/companies/:companyId/move', { companyId }),
    MOVE_CHECK: (companyId: UUID): string =>
      urlcat('vadmin/companies/:companyId/move_check', { companyId }),
    QUERY_BY_IDS_ADMIN: () => 'vadmin/company/query_by_ids',
    SETTINGS: {
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/vadmin_settings', { companyId }),
      /** @deprecated Use UPDATE_ADMIN endpoint instead */
      UPDATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/vadmin_settings', { companyId }),
    },
    SUPPLIER_ALERT_CONFIGS: {
      DELETE: (companyId: UUID): string =>
        urlcat('company/:companyId/alert_config', { companyId }),
      GET: (companyId: UUID): string =>
        urlcat('company/:companyId/alert_config', { companyId }),
      GET_ALL: () => 'company/sending_companies',
      UPDATE: (companyId: UUID): string =>
        urlcat('company/:companyId/alert_config', { companyId }),
    },
    SUPPLIER_LETTER: {
      DELETE: (companyId: UUID): string =>
        urlcat('vadmin/companies/:companyId/supplier_letter', { companyId }),
      GET: (companyId: UUID): string =>
        urlcat('vadmin/companies/:companyId/supplier_letter', { companyId }),
      PUT: (companyId: UUID): string =>
        urlcat('vadmin/companies/:companyId/supplier_letter', { companyId }),
    },
    UPDATE_ADMIN: (companyId: UUID): string =>
      urlcat('vadmin/company/:companyId', { companyId }),
    WATERMARK_SETTINGS: {
      GET: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/watermark_settings', { companyId }),
      UPDATE: (companyId: UUID): string =>
        urlcat('vadmin/company/:companyId/watermark_settings', { companyId }),
    },
  },
  CONTRACT_IMPORT_JOB: {
    GET_ALL: () => 'vadmin/contract-import-jobs',
  },
  COST_CENTER: {
    GET_ALL_ADMIN: () => 'vadmin/accounting_reference',
    GET_BULK_ADMIN: () => 'vadmin/accounting_reference/query/bulk',
  },
  CUSTOM_FIELD: { GET_BULK_ADMIN: () => 'vadmin/custom_field/query/bulk' },
  DATA_EXCHANGES: {
    COMPANIES: {
      GET_ALL: () => 'data_exchange/company_information/all',
      UPDATE: () => 'data_exchange/company_information',
    },
    DELETE: (dataExchangeId: UUID): string =>
      urlcat('data_exchange/delete/:dataExchangeId', { dataExchangeId }),
    GET_ALL: () => 'data_exchange/all',
    NOTION_FIELDS: {
      PATCH: (deId: UUID): string =>
        urlcat('vadmin/data_exchanges/:deId/notion_fields', { deId }),
    },
    UPDATE: () => 'data_exchange',
  },
  DATA_SYNC: {
    JOB_ADMIN: () => 'vadmin/sync/job',
    TASK_ADMIN: () => 'vadmin/sync/task',
  },
  DELIVERY: {
    ALLOWED_COMPANIES: {
      CREATE: () => 'vadmin/allowed_companies',
      DELETE: () => 'vadmin/allowed_companies',
    },
    DELETE_ADMIN: () => 'asset/delivery_note/admin/delete_dlns',
    GET_ADMIN: (deliveryNoteId: UUID): string =>
      urlcat('vadmin/asset/delivery_note/:deliveryNoteId', { deliveryNoteId }),
    GET_ALL_ADMIN: () => 'vadmin/asset/delivery_note/search',
    GET_ATTACHMENT_ADMIN: (deliveryNoteChainId: UUID): string =>
      urlcat('vadmin/asset/delivery_note/:deliveryNoteChainId/attachment', {
        deliveryNoteChainId,
      }),
    GET_BY_IDS_ADMIN: () => 'vadmin/asset/delivery_note/get_by_ids',
    GET_CHAINS_ADMIN: () => 'asset/delivery_note/ids/chains/admin',
    GET_COUNT_ADMIN: () => 'vadmin/asset/delivery_note/search/count',
    GET_FILTER_SUGGESTIONS: () => 'analytics/suggestions',
    GET_LATEST_ORIGINAL_PDF_ADMIN: (assetId: UUID): string =>
      urlcat('vadmin/asset/delivery_note/:assetId/original_pdf', {
        assetId,
      }),
    GET_PARTY_SUGGESTIONS_ADMIN: () =>
      'vadmin/delivery_note/suggestions/parties/match',
    PUSH_NOTIFICATION_DLN_CREATED: {
      UPDATE: (userId: UUID): string =>
        urlcat('user/:userId/settings', { userId }),
    },
  },
  FILE: {
    GET_UPLOAD_LINK: () => 'files/upload_link',
  },
  FTP_POLLER: {
    CREATE: () => 'vadmin/ftp_poller/secret',
    DELETE: (ftpPollerId: UUID): string =>
      urlcat('vadmin/ftp_poller/secret/:ftpPollerId', { ftpPollerId }),
    GET: (ftpPollerId: UUID): string =>
      urlcat('vadmin/ftp_poller/secret/:ftpPollerId', { ftpPollerId }),
    GET_ALL: () => 'vadmin/ftp_poller/secret',
    GET_FILE_COUNT: () => 'vadmin/ftp_poller/secret/file_count',
    UPDATE: (ftpPollerId: UUID): string =>
      urlcat('vadmin/ftp_poller/secret/:ftpPollerId', { ftpPollerId }),
  },
  ID_DICTIONARY: {
    ENTITY_ID: {
      GET: (entityId: UUID): string =>
        urlcat('vadmin/id_dictionary/:entityId', { entityId }),
    },
  },
  INTERNAL_USER: {
    MCP_WRITE_TOOLS: (userId: UUID): string =>
      urlcat('vadmin/internal_users/:userId/mcp_write_tools', { userId }),
    UPDATE_PERMISSION_LEVEL: (userId: UUID): string =>
      urlcat('vadmin/internal_users/:userId/permission_level', { userId }),
  },
  INVOICE_AI_MODEL: {
    SETTINGS: {
      CREATE: () => 'vadmin/invoice/ai_model_settings',
      CREATE_SENDER: (aiModelSettingsId: UUID) =>
        urlcat('vadmin/invoice/ai_model_settings/:aiModelSettingsId/sender', {
          aiModelSettingsId,
        }),
      DELETE: (aiModelSettingsId: UUID) =>
        urlcat('vadmin/invoice/ai_model_settings/:aiModelSettingsId', {
          aiModelSettingsId,
        }),
      DELETE_SENDER: (aiModelSettingsId: UUID, senderId: UUID) =>
        urlcat(
          'vadmin/invoice/ai_model_settings/:aiModelSettingsId/sender/:senderId',
          {
            aiModelSettingsId,
            senderId,
          },
        ),
      GET: (aiModelSettingsId: UUID) =>
        urlcat('vadmin/invoice/ai_model_settings/:aiModelSettingsId', {
          aiModelSettingsId,
        }),
      GET_ALL: () => 'vadmin/invoice/ai_model_settings',
      UPDATE: (aiModelSettingsId: UUID) =>
        urlcat('vadmin/invoice/ai_model_settings/:aiModelSettingsId', {
          aiModelSettingsId,
        }),
      UPDATE_SENDER: (aiModelSettingsId: UUID, senderId: UUID) =>
        urlcat(
          'vadmin/invoice/ai_model_settings/:aiModelSettingsId/sender/:senderId',
          {
            aiModelSettingsId,
            senderId,
          },
        ),
    },
  },
  INVOICE_FORWARDING: {
    CREATE: () => 'vadmin/invoice/auto_forward_rule',
    DELETE: (ruleId: UUID): string =>
      urlcat('vadmin/invoice/auto_forward_rule/:ruleId', { ruleId }),
    GET: (ruleId: UUID): string =>
      urlcat('vadmin/invoice/auto_forward_rule/:ruleId', { ruleId }),
    GET_ALL: () => 'vadmin/invoice/auto_forward_rule',
    GET_COMPANIES_BY_ALLOWED_INVOICE_SENDER: () =>
      'vadmin/invoice/companies_by_allowed_invoice_sender',
    UPDATE: (ruleId: UUID): string =>
      urlcat('vadmin/invoice/auto_forward_rule/:ruleId', { ruleId }),
  },
  INVOICE_REPROCESSING: {
    ADD_COMMENT: (processId: UUID): string =>
      urlcat('invoice/process/:processId/comment', { processId }),
    APPROVE_STOPPERS: (processId: UUID): string =>
      urlcat('invoice/process/:processId/approve_stoppers', { processId }),
    CREATE_FORWARDED_INVOICE_PREVIEW: (processId: UUID): string =>
      urlcat('invoice/process/:processId/invoice_preview', { processId }),
    CREATE_STOPPER_RULE: () => 'vadmin/invoice/stopper_rule',
    DELETE_STOPPER_RULE: (stopperRuleId: UUID): string =>
      urlcat('vadmin/invoice/stopper_rule/:stopperRuleId', { stopperRuleId }),
    DISMISS: (processId: UUID): string =>
      urlcat('invoice/process/:processId/dismiss', { processId }),
    GET: (processId: UUID): string =>
      urlcat('invoice/process/:processId', { processId }),
    GET_ALL: () => 'invoice/process',
    GET_COUNTS: () => 'invoice/process/aggregated',
    GET_EMAIL: (processId: UUID, azureId: UUID): string =>
      urlcat('invoice/process/:processId/mail/:azureId', {
        azureId,
        processId,
      }),
    GET_FILE: (processId: UUID, fileId: UUID): string =>
      urlcat('invoice/process/:processId/file/:fileId', {
        fileId,
        processId,
      }),
    GET_FORWARDING_REASONS: (processId: UUID): string =>
      urlcat('invoice/process/:processId/forward_reasons', { processId }),
    GET_PARSING_INPUT: (processId: UUID): string =>
      urlcat('invoice/process/:processId/parsing_input', { processId }),
    GET_PROCESSES_FROM_INVOICE_IDS: (): string =>
      'invoice/process/by_invoice_ids' as const,
    GET_STATUS: (processId: UUID): string =>
      urlcat('invoice/process/:processId/status', { processId }),
    GET_STOPPER_RULE: (stopperRuleId: UUID): string =>
      urlcat('vadmin/invoice/stopper_rule/:stopperRuleId', { stopperRuleId }),
    GET_STOPPER_RULES: () => 'vadmin/invoice/stopper_rule',
    GET_WATERMARKS: () => 'invoice/internal/watermarks',
    REPROCESS: (processId: UUID): string =>
      urlcat('invoice/process/:processId/reprocess', { processId }),
    SEND_INVOICE_PREVIEW: (processId: UUID, emailPreviewId: UUID): string =>
      urlcat(
        'invoice/process/:processId/invoice_preview/:emailPreviewId/send',
        {
          emailPreviewId,
          processId,
        },
      ),
    START_PROCESS_FROM_FILES: (): string => 'invoice/process/start_from_files',
    UPDATE_CURRENT_PARSED_INVOICE: (processId: UUID): string =>
      urlcat('invoice/process/:processId/current_parsed_invoice', {
        processId,
      }),
    UPDATE_PARSING_INPUT: (processId: UUID): string =>
      urlcat('invoice/process/:processId/parsing_input', { processId }),
    UPDATE_STOPPER_RULE: (stopperRuleId: UUID): string =>
      urlcat('vadmin/invoice/stopper_rule/:stopperRuleId', { stopperRuleId }),
  },
  INVOICE_SENDING_SETTINGS: {
    DELETE: (settingId: UUID): string =>
      urlcat('invoice/invoice_sending_settings/:settingId', { settingId }),
    QUERY_ALL: () => 'invoice/invoice_sending_settings/query_all',
    UPSERT: () => 'invoice/invoice_sending_settings',
  },
  NOTIFICATION: { SEND_CUSTOM_PUSH: () => 'push/custom' },
  PARSING_CASE: {
    BULK_DISMISS: () => 'vadmin/parsing_cases/actions/bulk_dismiss',
    BULK_RETRY: () => 'vadmin/parsing_cases/actions/bulk_retry',
    DISMISS: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId/actions/dismiss', { caseId }),
    GET: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId', { caseId }),
    GET_ALL: () => 'vadmin/parsing_cases',
    RETRY: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId/actions/retry', { caseId }),
    RETRY_PARSE: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId/actions/retry_parse', { caseId }),
    RETRY_PROCESS: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId/actions/retry_process', { caseId }),
    SAVE_DRAFT: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId/draft', { caseId }),
    SAVE_NOTE: (caseId: UUID): string =>
      urlcat('vadmin/parsing_cases/:caseId/note', { caseId }),
  },
  PDF_OVERLAY: {
    ACTIVATE_CONFIG: (settingId: UUID, configId: UUID): string =>
      urlcat(
        'vadmin/pdf_overlay/settings/:settingId/configs/:configId/activate',
        {
          configId,
          settingId,
        },
      ),
    CREATE_CONFIG_FOR_SETTING: (settingId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId/configs', {
        settingId,
      }),
    CREATE_SETTING: () => 'vadmin/pdf_overlay/settings',
    DEACTIVATE_SETTING: (settingId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId/deactivate', {
        settingId,
      }),
    DELETE_CONFIG: (settingId: UUID, configId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId/configs/:configId', {
        configId,
        settingId,
      }),
    DELETE_SETTING: (settingId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId', { settingId }),
    DUPLICATE_CONFIG: (settingId: UUID, configId: UUID): string =>
      urlcat(
        'vadmin/pdf_overlay/settings/:settingId/configs/:configId/duplicate',
        {
          configId,
          settingId,
        },
      ),
    GET_AVAILABLE_FIELDS: () => 'vadmin/pdf_overlay/fields',
    GET_CONFIG: (settingId: UUID, configId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId/configs/:configId', {
        configId,
        settingId,
      }),
    GET_EFFECTIVE_CONFIG: () => 'vadmin/pdf_overlay/settings/effective',
    GET_ORIGINAL_PDF: (assetId: UUID): string =>
      urlcat('vadmin/pdf_overlay/original_pdf/:assetId', { assetId }),
    GET_PREVIEW: (requestId: UUID): string =>
      urlcat('vadmin/pdf_overlay/preview/:requestId', {
        requestId,
      }),
    GET_SETTING: () => 'vadmin/pdf_overlay/settings',
    GET_SETTING_BY_ID: (settingId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId', { settingId }),
    LIST_CONFIGS: (settingId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId/configs', {
        settingId,
      }),
    LIST_SETTINGS: () => 'vadmin/pdf_overlay/settings/list',
    REQUEST_PREVIEW: (settingId: UUID, configId: UUID): string =>
      urlcat(
        'vadmin/pdf_overlay/settings/:settingId/configs/:configId/preview',
        {
          configId,
          settingId,
        },
      ),
    UPDATE_CONFIG: (settingId: UUID, configId: UUID): string =>
      urlcat('vadmin/pdf_overlay/settings/:settingId/configs/:configId', {
        configId,
        settingId,
      }),
  },
  PDF_TEMPLATE: {
    DOWNLOAD_DEFAULT_VERSION: (templateId: UUID): string =>
      urlcat('vadmin/default_pdf_template/download/:templateId', {
        templateId,
      }),
    GET_DEFAULT: () => 'vadmin/default_pdf_template',
    GET_DEFAULT_HISTORY: () => 'vadmin/default_pdf_template/history',
    RESTORE_DEFAULT_VERSION: (templateId: UUID): string =>
      urlcat('vadmin/default_pdf_template/restore/:templateId', {
        templateId,
      }),
    UPLOAD_DEFAULT: () => 'vadmin/default_pdf_template',
  },
  SITE: {
    GET_ALL_ADMIN: () => 'vadmin/site',
    GET_BULK_ADMIN: () => 'vadmin/site/query/bulk',
  },
  SUPPLIER_REQUEST: {
    GET: (reqId: UUID): string =>
      urlcat('vadmin/supplier_requests/:reqId', { reqId }),
    GET_ALL: () => 'vadmin/supplier_requests',
    PROCESS: (reqId: UUID): string =>
      urlcat('vadmin/supplier_requests/:reqId', { reqId }),
  },
  TEST_DLNS: { DELETE: () => 'vadmin/delete_test_dlns' },
  UNITS: {
    ADD_MAPPING: () => 'units/unit_mapping',
    DELETE_MAPPING: (unitMappingId: UUID): string =>
      urlcat('units/unit_mapping/:unitMappingId', { unitMappingId }),
    GET_ALL: () => 'units/dictionary',
    GET_ALL_MAPPINGS: () => 'units/unit_mapping',
    GET_MAPPING_BY_ISSUER_AND_IDENTIFIER: (): string =>
      'units/unit_mapping/query',
    UPDATE_MAPPING: (unitMappingId: UUID): string =>
      urlcat('units/unit_mapping/:unitMappingId', { unitMappingId }),
  },
  USER: {
    CREATE_ADMIN: () => 'vadmin/users',
    DELETE_IDENTITY_PROVIDER_LINK_ADMIN: (
      userId: UUID,
      identityProvider: string,
    ): string =>
      urlcat('vadmin/users/:userId/identity_provider_links/:identityProvider', {
        identityProvider,
        userId,
      }),
    GET_ADMIN: (userId: UUID): string =>
      urlcat('vadmin/user/:userId', { userId }),
    GET_ALL_ADMIN: () => 'vadmin/users',
    GET_ALL_IN_ACCOUNT: () => 'user/same_company_account',
    GET_BULK_ADMIN: () => 'vadmin/user/query/bulk',
    GET_IDENTITY_PROVIDER_LINKS_ADMIN: (userId: UUID): string =>
      urlcat('vadmin/users/:userId/identity_provider_links', { userId }),
    GET_ME: () => 'vadmin/user/me',
    SAVED_FILTER: (savedFilterId: UUID): string =>
      urlcat('user/saved_filters/:savedFilterId', { savedFilterId }),
    SAVED_FILTERS: () => 'user/saved_filters',
    SETTINGS_ADMIN: {
      GET: (userId: UUID): string =>
        urlcat('vadmin/user/:userId/settings', { userId }),
      UPDATE: (userId: UUID): string =>
        urlcat('vadmin/user/:userId/settings', { userId }),
    },
    UPDATE_ADMIN: (userId: UUID): string =>
      urlcat('vadmin/users/:userId', { userId }),
  },
} as const;
